import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { createApiKeyApi, getApiKeysApi, updateApiKeyStatusApi } from "../../redux/apiKey/apiKeyApi";
import { truncateText } from "../../helper/helper";

const ApiKeyLayer = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [clientName, setClientName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState("");

  const normalizedApiKeys = useMemo(() => {
    if (Array.isArray(apiKeys)) return apiKeys;
    return apiKeys?.items || [];
  }, [apiKeys]);

  const fetchApiKeys = async () => {
    const res = await getApiKeysApi();
    setApiKeys(res.data?.data || []);
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!clientName.trim()) return;

    try {
      setSaving(true);
      const res = await createApiKeyApi({ clientName: clientName.trim() });
      toast.success(res.data?.message || "API key created successfully");
      setClientName("");
      setShowModal(false);
      fetchApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create API key");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (apiKey) => {
    const apiKeyId = apiKey.id || apiKey.apiKeyId || apiKey._id;
    if (!apiKeyId) return;

    try {
      setUpdatingId(apiKeyId);
      const res = await updateApiKeyStatusApi(apiKeyId);
      toast.success(res.data?.message || "API key status updated successfully");
      fetchApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingId("");
    }
  };

  const handleCopy = async (value) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    toast.success("API key copied");
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <h6 className="mb-0 fw-semibold">API Keys</h6>
        <button
          type="button"
          className="btn btn-primary btn-sm d-flex align-items-center gap-2"
          onClick={() => setShowModal(true)}
        >
          <Icon icon="ic:baseline-plus" />
          Create Key
        </button>
      </div>

      <div className="card-body p-12">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Client Name</th>
                <th>API Key</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {normalizedApiKeys.map((apiKey, index) => {
                const apiKeyId = apiKey.id || apiKey.apiKeyId || apiKey._id;
                const status = apiKey.status ?? apiKey.isActive ?? apiKey.active;
                const keyValue = apiKey.key || apiKey.apiKey || "";

                return (
                  <tr key={apiKeyId || index}>
                    <td>{index + 1}</td>
                    <td>{apiKey.client|| "-"}</td>
                    <td>{truncateText(keyValue || "-", 40)}</td>
                    <td>
                      <label className="switch-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(status)}
                          disabled={updatingId === apiKeyId}
                          onChange={() => handleStatusChange(apiKey)}
                        />
                        <span className="slider" />
                      </label>
                    </td>
                    <td>{apiKey.createdAt ? new Date(apiKey.createdAt).toLocaleDateString() : "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                        onClick={() => handleCopy(keyValue)}
                      >
                        <Icon icon="solar:copy-outline" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {normalizedApiKeys.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    No API keys found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show d-block">
          <div className="modal-dialog modal-dialog-centered">
            <form className="modal-content radius-12 shadow-lg" onSubmit={handleCreate}>
              <div className="modal-header border-0">
                <h5 className="fw-semibold">Create API Key</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <div className="modal-body">
                <label className="form-label">Client Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter client name"
                />
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyLayer;
